/**
 * The Sentient Desk — Directive 1. Mandatory AI-persona review shown right
 * after a BUY click, before the order actually hits the market. Routes to
 * Kimani (Standard tier — deterministic execution clerk) or Obondo
 * (Premium tier — behavioral alpha advisor), whichever the backend
 * `/orders/review` endpoint resolves for the caller. Returns a single-use
 * `review_token` that must accompany the real `/orders` POST.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, fmtKES } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Sparkles, ShieldAlert, TrendingUp, Brain, Link as LinkIcon, Gauge } from "lucide-react";
import PrefGroup from "@/components/assistant/PrefGroup";

const VERDICT_STYLES = {
    green: { bg: "bg-green-500/10 border-green-500/40", text: "text-green-700 dark:text-green-400", label: "Green light" },
    amber: { bg: "bg-amber-500/10 border-amber-500/40", text: "text-amber-700 dark:text-amber-400", label: "Amber · caveat" },
    red:   { bg: "bg-red-500/10 border-red-500/40",     text: "text-red-700 dark:text-red-400",     label: "Red · consider stopping" },
};

// Obondo's explanation-depth toggle — reuses the SAME backend enum
// (simple/technical/enhanced) as Kimani's/Obondo's chat workspaces, just
// relabelled for the order-review context.
const REVIEW_DEPTH_OPTIONS = [
    { key: "simple",    label: "Basic" },
    { key: "technical", label: "Intermediate" },
    { key: "enhanced",  label: "Advanced" },
];

export function AiOrderReviewModal({ open, draft, onClose, onConfirm, busy }) {
    const [state, setState] = useState("loading"); // loading | ready | error
    const [review, setReview] = useState(null);
    const [depth, setDepth] = useState("technical");
    const [depthBusy, setDepthBusy] = useState(false);
    const navigate = useNavigate();

    const fetchReview = useCallback(async (draftArg) => {
        setState("loading");
        setReview(null);
        try {
            const { data } = await api.post("/orders/review", {
                symbol: draftArg.symbol, side: draftArg.side, qty: Number(draftArg.qty),
                account_id: draftArg.accountId,
                // Price-based SL/TP (Sept 2026) — % of instrument price is the
                // source of truth Obondo reviews against; KES sent only as a
                // legacy fallback when no % was set.
                stop_loss_pct: draftArg.stopLossPct ? Number(draftArg.stopLossPct) : undefined,
                take_profit_pct: draftArg.takeProfitPct ? Number(draftArg.takeProfitPct) : undefined,
                stop_loss_kes: (!draftArg.stopLossPct && draftArg.stopLoss) ? Number(draftArg.stopLoss) : undefined,
                take_profit_kes: (!draftArg.takeProfitPct && draftArg.takeProfit) ? Number(draftArg.takeProfit) : undefined,
                // Bug fix: Limit/Stop orders must be reviewed at the trader's
                // chosen trigger price, not the current live price — otherwise
                // Obondo's notional/fee/R:R commentary mismatches what actually
                // executes.
                order_type: draftArg.orderType || "market",
                trigger_price_kes: draftArg.triggerPrice ? Number(draftArg.triggerPrice) : undefined,
            });
            setReview(data);
            setState("ready");
        } catch (e) {
            // Blocked reviews now return HTTP 422 with the same JSON
            // shape as a "ready" pre_check_error response (previously
            // 200) — surface it the same way instead of the generic
            // "couldn't reach your trading desk" error state.
            const data = e?.response?.data;
            if (data?.pre_check_error) { setReview(data); setState("ready"); }
            else setState("error");
        }
    }, []);

    useEffect(() => {
        if (!open || !draft) return;
        let cancelled = false;
        // Default the depth toggle to the trader's saved preference before
        // firing the first review call.
        api.get("/chat/assistant/preferences")
            .then(({ data }) => { if (!cancelled) setDepth(data?.prefs?.depth || "technical"); })
            .catch(() => { /* non-fatal — keep the "technical" default */ });
        (async () => { if (!cancelled) await fetchReview(draft); })();
        return () => { cancelled = true; };
    }, [open, draft, fetchReview]);

    const onDepthChange = useCallback(async (next) => {
        if (next === depth || depthBusy || !draft) return;
        setDepth(next);
        setDepthBusy(true);
        try {
            await api.patch("/chat/assistant/preferences", { depth: next });
        } catch (e) {
            // Persisting the preference is best-effort — still re-run the
            // review at the newly-chosen depth even if the PATCH fails.
        }
        await fetchReview(draft);
        setDepthBusy(false);
    }, [depth, depthBusy, draft, fetchReview]);

    if (!open) return null;
    const isKimani = review?.persona === "kimani";
    const style = VERDICT_STYLES[review?.verdict] || VERDICT_STYLES.green;

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent className="max-w-md" data-testid="ai-order-review-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Sparkles size={18} className="text-primary" />
                        {review ? review.persona_label : "Routing to your trading desk…"}
                    </DialogTitle>
                </DialogHeader>

                {state === "loading" && (
                    <div className="py-8 flex flex-col items-center gap-2 text-sm text-muted-foreground" data-testid="ai-review-loading">
                        <Loader2 className="animate-spin" size={22} />
                        Reviewing this order…
                    </div>
                )}

                {state === "error" && (
                    <div className="py-6 text-sm text-center text-muted-foreground space-y-3" data-testid="ai-review-error">
                        <p>Couldn't reach your trading desk. Please try again.</p>
                        <button onClick={onClose} className="text-xs underline" data-testid="ai-review-error-close">Close</button>
                    </div>
                )}

                {state === "ready" && review?.pre_check_error && (
                    <div className="py-4 space-y-3" data-testid="ai-review-pre-check-error">
                        <div className="rounded-md border border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-400 text-sm p-3 font-medium">
                            {review.pre_check_error}
                        </div>
                        {review.pre_check_redirect ? (
                            <button
                                onClick={() => { onClose(); navigate(review.pre_check_redirect); }}
                                className="w-full min-h-[44px] text-xs font-bold px-3 py-2.5 rounded-md bg-primary text-primary-foreground hover:opacity-90 transition"
                                data-testid="ai-review-pre-check-cta"
                            >
                                Complete now
                            </button>
                        ) : null}
                        <button onClick={onClose} className="w-full min-h-[44px] text-xs px-3 py-2.5 rounded-md border border-border hover:bg-muted transition" data-testid="ai-review-pre-check-close">
                            {review.pre_check_redirect ? "Cancel" : "Fix and try again"}
                        </button>
                    </div>
                )}

                {state === "ready" && review && !review.pre_check_error && (
                    <div className="space-y-3" data-testid="ai-review-body" data-verdict={review.verdict || "green"}>
                        {isKimani ? (
                            <p className="text-sm leading-relaxed" data-testid="kimani-message">{review.message}</p>
                        ) : (
                            <>
                                <div className={`rounded-lg border p-3 ${style.bg}`}>
                                    <div className={`text-xs font-bold uppercase tracking-wide mb-1 ${style.text}`}>{style.label}</div>
                                    <div className="text-sm font-semibold" data-testid="obondo-headline">{review.headline}</div>
                                </div>
                                <div className="flex items-center justify-between gap-2" data-testid="obondo-depth-row">
                                    <PrefGroup
                                        label="Depth"
                                        testIdPrefix="obondo-review-depth"
                                        value={depth}
                                        options={REVIEW_DEPTH_OPTIONS}
                                        onChange={onDepthChange}
                                    />
                                    {depthBusy && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" data-testid="obondo-depth-loading" />}
                                </div>
                                <Link
                                    to="/app/copilot/obondo"
                                    onClick={onClose}
                                    className="text-[11px] text-primary underline hover:no-underline inline-block"
                                    data-testid="ai-review-compare-ideas-link"
                                >
                                    Want to test this against other ideas? Compare up to 3 in Obondo Coach →
                                </Link>
                                <div className="space-y-2 text-xs leading-relaxed">
                                    {review.risk_reward && (
                                        <div className="flex items-start gap-2" data-testid="obondo-rr">
                                            <TrendingUp size={12} className="mt-0.5 shrink-0 text-primary" />
                                            <span><b>R:R</b> · {review.risk_reward}</span>
                                        </div>
                                    )}
                                    {review.macro_note && (
                                        <div className="flex items-start gap-2" data-testid="obondo-macro">
                                            <LinkIcon size={12} className="mt-0.5 shrink-0 text-primary" />
                                            <span><b>Macro</b> · {review.macro_note}</span>
                                        </div>
                                    )}
                                    {review.bias_check && (
                                        <div className="flex items-start gap-2" data-testid="obondo-bias">
                                            <Brain size={12} className="mt-0.5 shrink-0 text-primary" />
                                            <span><b>Bias check</b> · {review.bias_check}</span>
                                        </div>
                                    )}
                                    {review.correlated_exposure && (
                                        <div className="flex items-start gap-2" data-testid="obondo-overlap">
                                            <ShieldAlert size={12} className="mt-0.5 shrink-0 text-primary" />
                                            <span><b>Book overlap</b> · {review.correlated_exposure}</span>
                                        </div>
                                    )}
                                    {review.margin_note && (
                                        <div className="flex items-start gap-2" data-testid="obondo-margin">
                                            <Gauge size={12} className="mt-0.5 shrink-0 text-primary" />
                                            <span><b>Margin</b> · {review.margin_note}</span>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}

                        <div className="rounded-md border border-border bg-muted/30 p-3 text-xs space-y-1" data-testid="ai-review-fee-breakdown">
                            <div className="flex justify-between"><span className="text-muted-foreground">Notional</span><span className="font-mono">{fmtKES(review.notional_kes)}</span></div>
                            <div className="flex justify-between"><span className="text-muted-foreground">Fee (2.0%)</span><span className="font-mono">{fmtKES(review.fee_kes)}</span></div>
                            <div className="flex justify-between font-semibold border-t border-border/60 pt-1 mt-1"><span>Cash required</span><span className="font-mono">{fmtKES(review.cash_required_kes)}</span></div>
                            {review.is_leveraged && <div className="text-primary pt-0.5">Leveraged position — only margin is debited.</div>}
                        </div>

                        {review.pre_liquidation_warning && (
                            <div className="rounded-md border border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-400 text-xs p-2.5 font-semibold" data-testid="pre-liquidation-warning">
                                ⚠ Pre-liquidation flag — account equity is within 5 points of the 25% maintenance-margin floor.
                            </div>
                        )}

                        <div className="flex gap-2 pt-1">
                            <button
                                onClick={onClose}
                                className="flex-1 min-h-[44px] text-xs px-3 py-2.5 rounded-md border border-border hover:bg-muted transition"
                                data-testid="ai-review-cancel"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => onConfirm(review.review_token)}
                                disabled={busy}
                                className="flex-[2] min-h-[44px] text-xs font-bold px-3 py-2.5 rounded-md bg-primary text-primary-foreground hover:opacity-90 transition disabled:opacity-50"
                                data-testid="ai-review-confirm"
                            >
                                {busy ? "Executing…" : review.require_confirmation ? "I understand — Confirm & Execute" : "Confirm & Execute"}
                            </button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default AiOrderReviewModal;
